import { useEffect, useState } from "react";
import { Globe, X } from "lucide-react";
import { useSettings, LANGUAGES } from "@/context/SettingsContext";

const PROMPTED_KEY = "pula_lang_prompted";
const SUPPORTED = new Set(LANGUAGES.map((l) => l.code));

// Offers first-time visitors to view Pula in their phone/browser language.
export default function LanguagePrompt() {
  const { language, setLanguage } = useSettings();
  const [detected, setDetected] = useState(null);

  useEffect(() => {
    if (localStorage.getItem(PROMPTED_KEY) === "1") return;
    const langs = navigator.languages && navigator.languages.length ? navigator.languages : [navigator.language || ""];
    let match = null;
    for (const l of langs) {
      const code = (l || "").split("-")[0].toLowerCase();
      if (code && code !== "en" && SUPPORTED.has(code)) { match = code; break; }
    }
    if (match && match !== language) {
      const info = LANGUAGES.find((x) => x.code === match);
      setDetected(info || null);
    } else {
      localStorage.setItem(PROMPTED_KEY, "1");
    }
  }, [language]);

  const dismiss = () => {
    localStorage.setItem(PROMPTED_KEY, "1");
    setDetected(null);
  };
  const accept = () => {
    localStorage.setItem(PROMPTED_KEY, "1");
    setLanguage(detected.code);
    setDetected(null);
  };

  if (!detected) return null;

  return (
    <div
      data-testid="language-prompt"
      data-no-translate
      className="fixed bottom-4 right-4 z-[70] max-w-xs w-[calc(100%-2rem)] sm:w-80 rounded-2xl border border-[var(--pula-border)] bg-white shadow-xl p-4 animate-in fade-in slide-in-from-bottom-2"
    >
      <button
        data-testid="language-prompt-close"
        onClick={dismiss}
        aria-label="Close"
        className="absolute top-2 right-2 p-1 rounded-full text-pula-inksoft hover:bg-black/5 transition-colors"
      >
        <X className="w-4 h-4" />
      </button>
      <div className="flex items-start gap-3">
        <span className="w-9 h-9 rounded-full bg-pula-green/15 grid place-items-center text-pula-green shrink-0">
          <Globe className="w-5 h-5" />
        </span>
        <div className="flex-1 pr-4">
          <p className="font-heading font-bold text-pula-ink text-sm">View Pula in {detected.label}?</p>
          <p className="text-xs text-pula-inksoft font-body mt-0.5">We can show the whole site in your language.</p>
          <div className="flex gap-2 mt-3">
            <button
              data-testid="language-prompt-accept"
              onClick={accept}
              className="px-3 py-1.5 rounded-full bg-pula-green hover:bg-pula-greenhover text-white text-xs font-body font-medium transition-colors"
            >
              Yes, {detected.label}
            </button>
            <button
              data-testid="language-prompt-decline"
              onClick={dismiss}
              className="px-3 py-1.5 rounded-full border border-pula-ink/20 text-pula-inksoft text-xs font-body font-medium hover:bg-black/5 transition-colors"
            >
              No thanks
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
